// Режимът „Схема" на „Маркиране" — чистата логика без React
// (тестове: orderingFloorData.test.ts). Сметката е свързана с маса от схемата
// САМО по името (`bills.name` = името на масата, docs/restaurant-schema.md);
// ключът е същият като в „Резервации" (`tableNameKey` — „2*" от офлайн
// replay е маса 2).

import type { BillOut, TakenTable } from "../../../../../api/bills";
import type { OpenTable } from "../../../../../api/reservations";
import type { SchemaElement, SchemaRegion } from "../../../../../api/restaurantSchema";
import { tableNameKey } from "../../../../reservations/logic/tableStatus";
import {
  averageLinePercentOf,
  customerDiscountOf,
  formatSignedBillPercent,
} from "../../billDiscount";

/** Маса от схемата и районът ѝ. */
export interface SchemaTableRef {
  table: SchemaElement;
  regionId: number;
}

/** Масите на всички райони по ключа на името. */
export type SchemaTableIndex = ReadonlyMap<string, SchemaTableRef>;

export const indexSchemaTables = (regions: SchemaRegion[]): SchemaTableIndex => {
  const index = new Map<string, SchemaTableRef>();
  for (const region of regions) {
    for (const element of region.elements) {
      if (element.kind !== "table" || !element.name) continue;
      const key = tableNameKey(element.name);
      if (!index.has(key)) index.set(key, { table: element, regionId: region.id });
    }
  }
  return index;
};

/**
 * Закрита без бон (сума 0) — нефискализирана: стои в списъка (по настройката
 * на ресторанта), а в режим „Схема" — САМО в сивата лента „Нефискализирани
 * маси": не заема маса, не е временна и не е сред сметките на масата в панела.
 */
export const isClosedEmptyBill = (bill: BillOut) =>
  bill.status === "closed" && parseFloat(bill.total) === 0;

/**
 * Сметките от `GET /bills` като отворени маси за снимката на залата
 * (`createFloorSnapshot`): отворените и тези, чието закриване още чака
 * принтера (сървърът може вече да ги връща закрити — масата е заета, докато
 * кутийката се върти). Закритата без бон не заема маса — и докато се закрива
 * повторно (`/close/retry`).
 */
export const billsAsOpenTables = (
  bills: BillOut[],
  closingBillIds: ReadonlySet<number>,
): OpenTable[] =>
  bills
    .filter(
      (bill) =>
        !isClosedEmptyBill(bill) &&
        (bill.status !== "closed" || closingBillIds.has(bill.id)),
    )
    .map((bill) => ({
      billId: bill.id,
      tableName: bill.name,
      openedAt: bill.restored_at ?? bill.created_at,
      waiterId: bill.waiter_id,
      waiterName: null,
      totalAmount: bill.total_amount,
      reservationId: bill.reservation?.id ?? null,
    }));

/**
 * Заетите маси на колегите от `GET /bills/taken-tables` (без право
 * `seat_reservations`) — като невидимите от „Настани": `hidden`, без сума.
 */
export const takenTablesAsOpenTables = (takenTables: TakenTable[]): OpenTable[] =>
  takenTables.map((taken) => ({
    billId: taken.billId,
    tableName: taken.tableName,
    openedAt: taken.openedAt,
    waiterId: taken.waiterId,
    waiterName: taken.waiterName,
    totalAmount: "0.00",
    reservationId: null,
    hidden: true,
  }));

/**
 * Своите (видимите) сметки + отворените, които списъкът НЕ вижда (чужди без
 * `view_all_users_bills`, `hidden` — от `GET /reservations/seatable` или
 * `takenTablesAsOpenTables`) — за да не изглеждат масите на колегите
 * свободни. Видимите идват само от списъка: той е по-пресен (току-що закрита
 * сметка иначе би държала масата заета до следващото опресняване).
 */
export const mergeOpenTables = (visible: OpenTable[], hiddenSource: OpenTable[]): OpenTable[] => {
  const visibleIds = new Set(visible.map((openTable) => openTable.billId));
  return [
    ...visible,
    ...hiddenSource.filter((openTable) => openTable.hidden && !visibleIds.has(openTable.billId)),
  ];
};

/**
 * Отворените маси + другите маси на „Една сметка" за резервация на няколко
 * маси (същата сметка под името на всяка маса) — сметката заема ВСИЧКИТЕ си
 * маси, и без резервациите (без право `seat_reservations` те не се четат).
 * `tableNamesOf` — масите, които сметката държи сама (`billTableNames`);
 * първата е името ѝ.
 */
export const withBillTables = (
  openTables: OpenTable[],
  tableNamesOf: (billId: number) => string[],
): OpenTable[] =>
  openTables.flatMap((openTable) => [
    openTable,
    ...tableNamesOf(openTable.billId)
      .slice(1)
      .map((tableName) => ({ ...openTable, tableName })),
  ]);

/** Масата от схемата на сметката (по името) или null — временна маса. */
export const schemaTableOf = (bill: BillOut, index: SchemaTableIndex): SchemaTableRef | null =>
  index.get(tableNameKey(bill.name)) ?? null;

/**
 * „Временни маси": сметките, чието име не е маса от НИТО ЕДИН район (отворени
 * от списъка с име извън схемата, „За вкъщи", „1А"…), без нефискализираните
 * (те са в сивата лента). Редът е като в списъка.
 */
export const temporaryBills = (bills: BillOut[], index: SchemaTableIndex): BillOut[] =>
  bills.filter((bill) => !isClosedEmptyBill(bill) && schemaTableOf(bill, index) === null);

/**
 * „Нефискализирани маси" (сивата лента): закритите без бон от списъка — ВСИЧКИ,
 * и с име на маса от схемата. Кои са и в какъв ред решава списъкът
 * (`useTableListBills` → `sortedBills`: настройката `nonFiscalDisplayMode`,
 * ALT+F1, закриващите се винаги) — тук нищо не се проверява наново.
 */
export const nonFiscalBills = (bills: BillOut[]): BillOut[] => bills.filter(isClosedEmptyBill);

/**
 * Сметките на една маса от схемата, в реда на списъка: по името ѝ; маса без
 * своя сметка — тези, които я държат без името ѝ (`linkedBillIds` — на
 * настанената резервация, „Една сметка" на няколко маси). Маса със своя
 * сметка („Отделна сметка за всяка маса") НЕ взима сметките на другите маси на
 * резервацията — иначе кликът би избрал чужда маса. Без нефискализираните —
 * те са в сивата лента.
 */
export const billsOnTable = (
  table: SchemaElement,
  bills: BillOut[],
  linkedBillIds: readonly number[] = [],
): BillOut[] => {
  const key = tableNameKey(table.name ?? "");
  const open = bills.filter((bill) => !isClosedEmptyBill(bill));
  const named = open.filter((bill) => tableNameKey(bill.name) === key);
  return named.length > 0 ? named : open.filter((bill) => linkedBillIds.includes(bill.id));
};

/**
 * Знакът горе вдясно на масата — същият процент като баджа на картата в
 * списъка: на закачения клиент („-50%" / „+10%"), иначе средният от
 * отстъпките/надбавките по редовете (`averageLinePercentOf` — без „Промяна
 * на цена"); null — без знак (и при клиент с 0% без корекции по редовете).
 */
export const discountMarkOf = (bill: BillOut): string | null => {
  const clientPercent = customerDiscountOf(bill)?.percent ?? 0;
  const percent = clientPercent || averageLinePercentOf(bill);
  return percent !== 0 ? formatSignedBillPercent(percent) : null;
};

/**
 * Коя сметка се избира при клик по маса: отворена (закритата без бон е в
 * сивата лента — тук е само предпазна проверка), първо своята (лична
 * номерация — няколко „1"), иначе първата по реда на списъка. Няма ли
 * отворена — null (масата е свободна).
 */
export const preferredBillOf = (bills: BillOut[], currentUserId: number | null): BillOut | null => {
  const open = bills.filter((bill) => !isClosedEmptyBill(bill));
  return open.find((bill) => bill.waiter_id === currentUserId) ?? open[0] ?? null;
};

/**
 * Масите от схемата, на които „седи" сметката: по името ѝ и всички маси на
 * настанената ѝ резервация (`reservation.table_names`) — ореолът и проверката
 * дали избраната маса още е на тази сметка. Нефискализираната не „седи" на
 * маса (избира се от сивата лента) — празно.
 */
export const tableIdsOfBill = (bill: BillOut, index: SchemaTableIndex): number[] => {
  if (isClosedEmptyBill(bill)) return [];
  const names = [bill.name, ...(bill.reservation?.table_names ?? [])];
  const ids = names.flatMap((name) => {
    const ref = index.get(tableNameKey(name));
    return ref ? [ref.table.id] : [];
  });
  return [...new Set(ids)];
};

/**
 * Имената на масите, които сметката държи сама: нейната и — при „Една сметка"
 * за НАСТАНЕНА резервация на няколко маси — другите маси на резервацията без
 * тези с друга нейна сметка („Отделна сметка за всяка маса", закачена — и
 * закрита). Смята ги BE (`table_names` в `GET /bills`): списъкът не вижда
 * закритите и чуждите сметки. Името на картата и в заглавието на панела
 * („14 | 13 | 12"), заетите маси и „Закрива се" на всичките. Иначе — името.
 */
export const billTableNames = (bill: BillOut): string[] =>
  bill.reservation && bill.table_names && bill.table_names.length > 1 ? bill.table_names : [bill.name];

// ── Полето „Сервитьор" (горе вдясно, право `view_all_users_bills`) ───────────

/**
 * „Ничията" сметка от „Настани": отворена, на настанена резервация, без нито
 * един ред — BE я дава на всеки (`pending_seat_bill_clause`), поема я първият
 * поръчал. Флаг от BE няма — приближение (лъжливо „да" е безвредно: празна
 * сметка на настанена резервация остава видима).
 */
export const isPendingSeatBill = (bill: BillOut) =>
  bill.status === "open" &&
  bill.reservation != null &&
  bill.elements.every((element) => element.items.length === 0);

/** Сметка без сервитьор — показва се при всеки избран сервитьор. */
const isUnownedBill = (bill: BillOut) => bill.waiter_id === null || isPendingSeatBill(bill);

/** Сметката е на избрания сервитьор (или е ничия). */
export const billShownForWaiter = (bill: BillOut, waiterId: number) =>
  bill.waiter_id === waiterId || isUnownedBill(bill);

/** Сервитьорът, когото полето следва за избраната сметка (ничия — никого). */
export const followedWaiterOf = (bill: BillOut | null): number | null =>
  bill && !isUnownedBill(bill) ? bill.waiter_id : null;

/**
 * Сервитьорите в полето: влезлият (винаги, пръв) + всеки със сметка в
 * списъка (отворена или нефискализирана — каквото списъкът показва) + този на
 * избраната сметка. Ничиите не правят никого опция.
 */
export const waiterIdsWithBills = (
  bills: BillOut[],
  currentUserId: number,
  selectedBill: BillOut | null,
): number[] => {
  const ids = new Set<number>([currentUserId]);
  for (const bill of selectedBill ? [...bills, selectedBill] : bills) {
    if (!isUnownedBill(bill) && bill.waiter_id !== null) ids.add(bill.waiter_id);
  }
  return [...ids];
};

/** Влезлият пръв, после останалите по азбучен ред (като „Смяна на сервитьор"). */
export const orderWaiterOptions = <Option extends { waiterId: number; name: string | null }>(
  options: Option[],
  currentUserId: number,
): Option[] =>
  [...options].sort((left, right) => {
    if (left.waiterId === currentUserId) return -1;
    if (right.waiterId === currentUserId) return 1;
    return (left.name ?? "").toLowerCase().localeCompare((right.name ?? "").toLowerCase());
  });

/**
 * Скритата чужда сметка на масата (гледащият без право — от „Настани" или
 * `GET /bills/taken-tables`) — по името ѝ (и по сметките на резервация, ако
 * са подадени); null — няма. Такава маса е заета: в панела — само чия е, а
 * „Нова маса" е заключен.
 */
export const hiddenOpenTableFor = (
  openTables: OpenTable[],
  tableName: string,
  reservationBillIds: readonly number[] = [],
): OpenTable | null => {
  const key = tableNameKey(tableName);
  return (
    openTables.find((openTable) => openTable.hidden && tableNameKey(openTable.tableName) === key) ??
    openTables.find((openTable) => openTable.hidden && reservationBillIds.includes(openTable.billId)) ??
    null
  );
};
